import React from 'react';
import styled from 'styled-components';
import isHtml from 'is-html';
import { ErrorMessage, WarningMessage } from 'Components/Common/styled';
import { PasswordEyeIcon, PasswordEyeStrikeIcon } from 'assets/icons';
import { BREAKPOINTS } from 'constants/common';

const InputStyled = styled.input<{ error?: boolean; warning?: boolean }>`
  width: 100%;
  padding: 14px 8px 14px;
  border: 1px solid ${props => (props.error ? 'var(--errorRed)' : 'var(--defaultBorderColor)')};
  background-color: 'var(--white)';
  color: ${props => (props.error ? 'var(--errorRed)' : 'var(--black)')};
  border-radius: 4px;
  font-family: 'Inter';
  font-size: 16px;
  font-weight: 400;
  box-sizing: border-box;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    padding: 14px 8px 14px;
  }

  &:focus {
    outline: none;
    border: 1px solid ${props => (props.error ? 'var(--errorRed)' : 'var(--primary)')};
    background-color: var(--white) !important;
    box-sizing: border-box;
    box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
    border: 1px solid var(--Primary-P500, #4f31ff);
    background: var(--Shade-White, #fff);
  }

  &::placeholder {
    color: ${props => (props.error ? 'var(--errorRed)' : 'var(--gray)')};
    opacity: 1;
  }

  // error when only space characters
  &:not(:placeholder-shown) {
    border-color: var(--errorRed);
    background-color: var(--errorLightBackground);
  }

  &:disabled,
  &:valid {
    border-color: ${props =>
      props.error || props.warning ? 'var(--errorRed)' : 'var(--defaultBorderColor)'};
    background-color: ${props => {
      if (props.error) return 'var(--errorLightBackground)';

      return props.warning ? 'var(--white)' : 'var(--lightBlueBackground)';
    }};

    &:focus {
      border-color: var(--primary);
    }
  }

  &:disabled {
    opacity: 0.4;
  }
`;

const PasswordInputWrap = styled.div`
  position: relative;

  svg {
    position: absolute;
    right: 17px;
    top: 25px;
    width: 24px;
    height: 16px;
    cursor: pointer;
  }

  /* safari only */
  @media not all and (min-resolution: 0.001dpcm) {
    @supports (-webkit-appearance: none) {
      svg {
        right: 40px;
      }
    }
  }
`;

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  type: 'text' | 'password' | 'number';
  error?: string;
  autoComplete?: string;
  warning?: string;
  placeholder?: string;
}

const Input: React.FC<InputProps> = ({
  type,
  error,
  warning,
  autoComplete,
  placeholder,
  ...props
}) => {
  const [revealPassword, setRevealPassword] = React.useState<boolean>(false);
  const handleClickPasswordEye = () => setRevealPassword(!revealPassword);

  if (type === 'password') {
    return (
      <PasswordInputWrap>
        {revealPassword ? (
          <PasswordEyeIcon data-testid="passwordEye" onClick={handleClickPasswordEye} />
        ) : (
          <PasswordEyeStrikeIcon data-testid="passwordEyeStrike" onClick={handleClickPasswordEye} />
        )}
        <InputStyled
          required
          autoComplete={autoComplete}
          pattern=".*\S.*"
          placeholder={placeholder}
          type={revealPassword ? 'text' : 'password'}
          error={!!error}
          {...props}
        />
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </PasswordInputWrap>
    );
  }
  return (
    <>
      <InputStyled
        required
        pattern=".*\S.*"
        placeholder={placeholder}
        autoComplete={autoComplete}
        type={type}
        error={!!error}
        warning={!error && !!warning}
        {...props}
      />
      {error && (
        <ErrorMessage>
          {isHtml(error) ? <div dangerouslySetInnerHTML={{ __html: error }}></div> : error}
        </ErrorMessage>
      )}
      {!error && warning && <WarningMessage>{warning}</WarningMessage>}
    </>
  );
};

export default Input;
